import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { isRfc3339WithOffset, rfc3339ToDate } from "@workspace/datetime";
import { tasks } from "../drizzle/tasks.js";
import { stepWithTypeSchema } from "./steps.js";
export const taskSchema = createSelectSchema(tasks, {
    id: (schema) => schema.meta({
        description: "Task ID",
        example: 1,
    }),
    // Use z.coerce.date() for date fields because JSON serialization converts Date objects to ISO strings
    createdAt: () => z.coerce.date().meta({
        description: "Task creation timestamp",
        example: "2024-01-15T10:30:00.000Z",
    }),
    updatedAt: () => z.coerce.date().meta({
        description: "Task last update timestamp",
        example: "2024-01-15T10:30:00.000Z",
    }),
    createdBy: (schema) => schema.meta({
        description: "User ID who created this task (Keycloak UUID)",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    lastUpdatedBy: (schema) => schema.meta({
        description: "User ID who last updated this task (Keycloak UUID)",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    type: (schema) => schema.meta({
        description: "Task type key (references taskTypes.key), the workflow of the task",
        example: "cost_estimate",
    }),
    channel: (schema) => schema.meta({
        description: "How the task arrived in ePort",
        example: "email",
    }),
    department: (schema) => schema.meta({
        description: "The part of the operation that handles the task; null when its task type has no department split",
        example: null,
    }),
    bucket: (schema) => schema.nullable().meta({
        description: "Bucket ID that this task belongs to (null if unassigned)",
        example: 1,
    }),
    title: (schema) => schema.meta({
        description: "Task title",
        example: "Implement authentication",
    }),
    description: (schema) => schema.meta({
        description: "Task description",
        example: "Add JWT authentication to the API",
    }),
    estimatedTime: (schema) => schema.meta({
        description: "Estimated time to complete task in minutes",
        example: 60,
    }),
    skillLevel: (schema) => schema.meta({
        description: "Required skill level",
        example: "intermediate",
    }),
    priority: (schema) => schema.meta({
        description: "Task priority (higher = more important)",
        example: 5,
    }),
    status: (schema) => schema.meta({
        description: "Task status",
        example: "in_progress",
    }),
    assignee: (schema) => schema.meta({
        description: "User ID assigned to this task (Keycloak UUID)",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    previousAssignees: (schema) => schema.meta({
        description: "User IDs (Keycloak UUIDs) that previously held this task and skipped it; never re-assigned to the same user",
        example: ["afe710c0-595d-4e4e-ab70-7c5900a14457"],
    }),
    startTime: () => z.coerce.date().nullable().meta({
        description: "Actual start time of the task",
        example: "2024-01-15T10:30:00.000Z",
    }),
    endTime: () => z.coerce.date().nullable().meta({
        description: "Actual end time of the task",
        example: "2024-01-15T12:30:00.000Z",
    }),
    duration: (schema) => schema.meta({
        description: "Actual task duration in minutes",
        example: 120,
    }),
}).describe("Task object");
// Base schema using callbacks - stays in sync with Drizzle column types
const baseCreateTaskSchema = createInsertSchema(tasks, {
    createdBy: () => z.uuid({ message: "Bitte gib eine gültige Ersteller-ID an" }).meta({
        description: "User ID who created this task (Keycloak UUID)",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    lastUpdatedBy: (schema) => schema.nullable().meta({
        description: "User ID who last updated this task (Keycloak UUID)",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    type: (schema) => schema.meta({
        description: "Task type key (references taskTypes.key)",
        example: "cost_estimate",
    }),
    bucket: () => z
        .preprocess((val) => (val === undefined || Number.isNaN(val) ? null : val), z.number().min(1, "Bucket-ID muss eine positive Zahl sein").nullable())
        .meta({
        description: "Bucket ID that this task belongs to (null if unassigned)",
        example: 1,
    }),
    title: (schema) => schema.min(1, "Bitte gib einen Titel an").meta({
        description: "Task title",
        example: "Implement authentication",
    }),
    description: (schema) => schema.nullable().meta({
        description: "Task description",
        example: "Add JWT authentication to the API",
    }),
    estimatedTime: (schema) => schema.meta({
        description: "Estimated time to complete task in minutes",
        example: 60,
    }),
    skillLevel: (schema) => schema.min(1, "Bitte gib ein Skill Level an").meta({
        description: "Required skill level",
        example: "intermediate",
    }),
    priority: (schema) => schema.meta({
        description: "Task priority (higher = more important)",
        example: 5,
    }),
    status: (schema) => schema.meta({
        description: "Task status",
        example: "pending",
    }),
    assignee: (schema) => schema.nullable().meta({
        description: "User ID assigned to this task (Keycloak UUID)",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    previousAssignees: (schema) => schema.meta({
        description: "User IDs (Keycloak UUIDs) that previously held this task and skipped it; never re-assigned to the same user",
        example: ["afe710c0-595d-4e4e-ab70-7c5900a14457"],
    }),
    startTime: () => z
        .string()
        .refine(isRfc3339WithOffset, "Expected RFC 3339 datetime with offset")
        .transform((val) => rfc3339ToDate(val))
        .nullable()
        .meta({
        description: "Actual start time (RFC 3339 with offset)",
        example: "2026-01-15T10:30:00+01:00",
    }),
    endTime: () => z
        .string()
        .refine(isRfc3339WithOffset, "Expected RFC 3339 datetime with offset")
        .transform((val) => rfc3339ToDate(val))
        .nullable()
        .meta({
        description: "Actual end time (RFC 3339 with offset)",
        example: "2026-01-15T12:30:00+01:00",
    }),
    duration: (schema) => schema.nullable().meta({
        description: "Actual task duration in minutes",
        example: 120,
    }),
});
// Fix drizzle-zod bug: callbacks for nullable fields lose .optional() wrapper
// We extend the base schema to add .optional() to nullable fields and fields with DB defaults
export const createTaskSchema = baseCreateTaskSchema
    .extend({
    lastUpdatedBy: baseCreateTaskSchema.shape.lastUpdatedBy.optional(),
    bucket: baseCreateTaskSchema.shape.bucket.optional(),
    description: baseCreateTaskSchema.shape.description.optional(),
    estimatedTime: baseCreateTaskSchema.shape.estimatedTime.optional(), // DB default: 0
    assignee: baseCreateTaskSchema.shape.assignee.optional(),
    previousAssignees: baseCreateTaskSchema.shape.previousAssignees.optional(), // DB default: '[]'
    startTime: baseCreateTaskSchema.shape.startTime.optional(),
    endTime: baseCreateTaskSchema.shape.endTime.optional(),
    duration: baseCreateTaskSchema.shape.duration.optional(),
})
    .describe("Create task request body");
// Task with actual steps response schema
export const taskWithStepsAndStepTypesSchema = taskSchema.extend({
    steps: stepWithTypeSchema.array(),
});
